import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Email from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { forgotPassword } from 'Shared/Security/Domain/Services/PasswordResetService';

export default function ForgotPasswordDialog({ open, onClose, initialEmail = '' }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setEmail(initialEmail || '');
      setSuccess(false);
      setError(null);
      setLoading(false);
    }
  }, [open, initialEmail]);

  const handleEmailBlur = () => {
    let finalEmail = email.trim();
    if (finalEmail) {
      if (!finalEmail.includes('@')) {
        setEmail(`${finalEmail}@aszende.com`);
      } else if (finalEmail.endsWith('@')) {
        setEmail(`${finalEmail}aszende.com`);
      } else if (finalEmail.endsWith('@aszende')) {
        setEmail(`${finalEmail}.com`);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let finalEmail = email.trim();
    if (!finalEmail || loading) {
      return;
    }

    if (!finalEmail.includes('@')) {
      finalEmail = `${finalEmail}@aszende.com`;
    } else if (finalEmail.endsWith('@')) {
      finalEmail = `${finalEmail}aszende.com`;
    } else if (finalEmail.endsWith('@aszende')) {
      finalEmail = `${finalEmail}.com`;
    }
    setEmail(finalEmail);

    setLoading(true);
    setError(null);

    try {
      await forgotPassword(finalEmail);
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || t('security.forgotPassword.error');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1.25rem' }}>
        {t('security.forgotPassword.title')}
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {t('security.forgotPassword.description')}
          </Typography>

          {success ? (
            <Alert
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              severity="success"
              sx={{ mt: 1, mb: 1, borderRadius: 2 }}
            >
              {t('security.forgotPassword.success')}
            </Alert>
          ) : (
            <>
              <TextField
                margin="dense"
                id="forgot-email"
                name="email"
                label={t('security.login.email')}
                type="email"
                fullWidth
                variant="outlined"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={onClose}
            color="inherit"
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {success ? t('security.resetPassword.backToLogin') : t('security.forgotPassword.cancel')}
          </Button>

          {!success && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !email}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
              sx={{ textTransform: 'none', px: 3 }}
            >
              {t('security.forgotPassword.send')}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

ForgotPasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialEmail: PropTypes.string
};
